
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Loader2, LogIn, Zap, ShieldCheck, Home, Eye, EyeOff } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { phpApi, isAdminAuthenticated } from "@/integrations/php/client";

const AdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const from = location.state?.from?.pathname || "/admin";

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authenticated = await isAdminAuthenticated();
        setIsAuthenticated(authenticated);
        if (authenticated) {
          navigate(from, { replace: true });
        }
      } catch (error) {
        console.error("Authentication check error:", error);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [from, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalizedUsername = username.trim();

    if (!normalizedUsername || !password) {
      toast({
        title: "Σφάλμα",
        description: "Παρακαλώ συμπληρώστε όλα τα πεδία",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await phpApi.functions.invoke('admin-auth', {
        body: { username: normalizedUsername, password }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error('Σφάλμα σύνδεσης');
      }

      if (!data.success) {
        throw new Error(data.message || 'Λάθος όνομα χρήστη ή κωδικός');
      }

      // Session is set server-side; store CSRF token + username for UI
      if (data.csrf_token) {
        sessionStorage.setItem('csrf_token', data.csrf_token);
      }
      if (data.username) {
        sessionStorage.setItem('adminUsername', data.username);
      }

      toast({
        title: "Επιτυχής Σύνδεση",
        description: `Καλώς ήρθες, ${data.username}!`,
      });

      navigate(from, { replace: true });

    } catch (error) {
      console.error("Login error:", error);
      toast({
        title: "Σφάλμα Σύνδεσης",
        description: error instanceof Error ? error.message : "Παρουσιάστηκε σφάλμα κατά τη σύνδεση",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-login for development (Test Admin)
  const handleAutoLogin = async () => {
    if (!import.meta.env.DEV) return;

    setUsername("superadmin");
    setPassword("Admin@12345");
    
    // Small delay to show the fields being filled
    setTimeout(async () => {
      setIsLoading(true);
      
      try {
        const { data, error } = await phpApi.functions.invoke('admin-auth', {
          body: { username: "superadmin", password: "Admin@12345" }
        });

        if (error) {
          console.error('Edge function error:', error);
          throw new Error('Σφάλμα σύνδεσης');
        }

        if (!data.success) {
          throw new Error(data.message || 'Λάθος όνομα χρήστη ή κωδικός');
        }

        if (data.csrf_token) {
          sessionStorage.setItem('csrf_token', data.csrf_token);
        }
        if (data.username) {
          sessionStorage.setItem('adminUsername', data.username);
        }

        toast({
          title: "Αυτόματη Σύνδεση",
          description: `Καλώς ήρθες, ${data.username}! (Test Mode)`,
        });

        navigate(from, { replace: true });

      } catch (error) {
        console.error("Auto-login error:", error);
        toast({
          title: "Σφάλμα Αυτόματης Σύνδεσης",
          description: error instanceof Error ? error.message : "Παρουσιάστηκε σφάλμα",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }, 300);
  };

  if (isAuthenticated === null) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <div className="relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-16 w-16 rounded-full bg-blue-500/20 animate-ping"></div>
            </div>
            <Loader2 className="relative h-12 w-12 animate-spin text-blue-400" />
          </div>
          <p className="mt-6 text-lg font-medium text-slate-300">Έλεγχος πρόσβασης...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl"></div>

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/25">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Lead Manager</h1>
            <p className="text-xs text-slate-400">Διαθερμική</p>
          </div>
        </div>

        {/* Main content */}
        <div className="relative z-10 space-y-6">
          <h2 className="text-4xl font-bold text-white leading-tight">
            Διαχειριστείτε τα<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
              leads σας εύκολα
            </span>
          </h2>
          <p className="text-lg text-slate-400 max-w-md">
            Παρακολουθήστε επαφές, διαχειριστείτε συνεργάτες και βελτιστοποιήστε την απόδοση της ομάδας σας.
          </p>

          <div className="flex items-center gap-4 pt-4">
            <div className="flex items-center gap-2 text-slate-400">
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
              <span className="text-sm">Ασφαλής πρόσβαση</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-sm text-slate-500 relative z-10">
          © {new Date().getFullYear()} Διαθερμική. Όλα τα δικαιώματα διατηρούνται.
        </p>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 via-white to-blue-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center justify-center gap-3 mb-8 lg:hidden">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">Lead Manager</span>
          </div>

          <Card className="border-0 shadow-xl shadow-slate-200/50">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-center text-slate-900">
                Καλώς ήρθατε
              </CardTitle>
              <CardDescription className="text-center text-slate-500">
                Εισάγετε τα στοιχεία σας για πρόσβαση
              </CardDescription>
            </CardHeader>

            <form onSubmit={handleLogin}>
              <CardContent className="space-y-4 pb-6">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-slate-700">
                    Όνομα χρήστη ή Email
                  </Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="admin@example.com"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={isLoading}
                    required
                    className="h-11 bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">
                    Κωδικός
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                      required
                      className="h-11 bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPassword(prev => !prev)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 text-slate-500 hover:text-slate-700"
                      aria-label={showPassword ? "Απόκρυψη κωδικού" : "Εμφάνιση κωδικού"}
                      disabled={isLoading}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>

              <CardContent className="pt-0">
                <Button
                  type="submit"
                  className="w-full h-11 btn-gradient"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Σύνδεση...
                    </>
                  ) : (
                    <>
                      <LogIn className="mr-2 h-4 w-4" />
                      Σύνδεση
                    </>
                  )}
                </Button>
                
                {import.meta.env.DEV && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-10 text-xs border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900"
                      onClick={handleAutoLogin}
                      disabled={isLoading}
                    >
                      <Zap className="mr-2 h-3 w-3" />
                      Αυτόματη Είσοδος (Test Admin)
                    </Button>
                  </div>
                )}
              </CardContent>
            </form>
          </Card>

          {/* Back to Home Button - Local Development */}
          <div className="mt-4 text-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-900 border-slate-300"
            >
              <Home className="mr-2 h-4 w-4" />
              Πίσω στην Αρχική
            </Button>
          </div>

          <p className="mt-6 text-center text-sm text-slate-500">
            Χρειάζεστε βοήθεια;{" "}
            <a href="mailto:support@diathermiki.gr" className="text-blue-600 hover:text-blue-700 font-medium">
              Επικοινωνήστε μαζί μας
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
